import FullWidthBarBreadcrumbs from '@/components/Breadcrumb/FullWidthBarBreadcrumbs';
import CenteredContentSection from '@/components/ContentSection/CenteredContentSection';
import { H3, H4, H5 } from '@/components/Element/Heading';
import MetaTags from '@/components/Head/MetaTags';
import Hero from 'images/solar-customer-resources/FederalTaxCreditGuide.png';
import CreditsAmounts from 'images/solar-customer-resources/CreditAmounts.png';
import Image from 'next/legacy/image';
import Link from 'next/link';
import FullWidthGuideFormSection from '@/components/LeadForm/FullWidthGuideForm';
import ReadingTime from '@/components/ReadingTime/ReadingTime';

export default function Page() {
  return (
    <article>
      <MetaTags
        title="Federal Solar Tax Credit Guide"
        description={`The residential solar energy credit is valued at 30% of the installed system costs until 2032, decreasing to 26% in 2033, and further decreasing to 22% in 2034 before expiring.`}
      />
      <FullWidthBarBreadcrumbs
        pages={[
          { name: 'Resources', href: '/solar-customer-resources' },
          {
            name: 'Federal Solar Tax Credit Guide',
            href: '/federal-solar-tax-credit',
          },
        ]}
      />
      <CenteredContentSection>
        <Image
          className="drop-shadow-lg rounded-md"
          src={Hero}
          alt="Federal Solar Tax Credit Guide"
        />
        <ReadingTime wordCount={986} />
        <H3>Federal Solar Tax Credit Guide</H3>
        <div className="bg-slate-100 rounded-md p-4">
          <H5 className="text-2xl font-semibold">
            <strong>Key Takeaways</strong>
          </H5>
          <ul>
            <li>
              If you purchase (not lease) new solar equipment for electricity
              generation, water heating, or energy storage, you can often use
              the Residential Clean Energy Credit to reduce your tax liability
            </li>
            <li>
              To qualify, these energy-saving enhancements must be made to your
              primary US residence, which can include various types of homes
              like houses, mobile homes, cooperative apartments, condos,
              manufactured homes, or houseboats.
            </li>
            <li>
              The credit may also apply to a vacation home, but only for the
              portion of the tax year you use it as a residence.
            </li>
            <li>
              To claim the Federal Solar Tax Credit, you&apos;ll need to fill
              out IRS Form 5695 and include the result on IRS Form 1040.
            </li>
            <li>
              The residential solar energy credit is valued at 30% of the
              installed system costs until 2032, decreasing to 26% in 2033, and
              further decreasing to 22% in 2034 before expiring.
            </li>
          </ul>
        </div>
        <H4>What Is a Tax Credit?</H4>
        <p>
          Let&apos;s begin with understanding what{' '}
          <strong>
            a tax credit is. A tax credit is a dollar-for-dollar reduction in
            your tax liability.
          </strong>{' '}
          This isn&apos;t just a deduction from your taxable income, but a
          credit subtracted from your tax bill directly. So, if you owe $8,000
          in taxes and receive a $5,000 tax credit, you end up owing just
          $3,000.
        </p>
        <H4>What Is the Federal Solar Tax Credit?</H4>
        <p>
          The Federal Solar Tax Credit, also known as the{' '}
          <strong>Investment Tax Credit (ITC)</strong>, is a U.S. government
          initiative. It&apos;s designed to financially support homeowners and
          businesses that want to install solar energy systems.
        </p>
        <H4>How Does the Federal Solar Tax Credit Work?</H4>
        <p>
          The Solar Tax Credit allows you to deduct a percentage of the cost of
          installing a solar energy system from your federal taxes. For
          instance, if your solar system cost $20,000 and the tax credit rate is
          30%, you could reduce your federal tax liability by $6,000.
        </p>
        <p>
          The percentage you can deduct has changed over time. Right now it is
          30%, then reduced to 26% for 2033 and 22% in 2034.
        </p>
        <Image src={CreditsAmounts} alt="Solar investment tax credit chart" />
        <H4>Federal Solar Tax Credit Eligibility</H4>
        <p>
          <strong>Who is eligible for the solar tax credit?</strong> If you own
          your home or commercial property and purchase your solar energy system
          outright (either with cash or a loan), you&apos;re eligible. Leased
          systems or ones under a power purchase agreement (PPA) don&apos;t
          qualify because you don&apos;t own the system.
        </p>
        <p>
          Learn more about solar financing here:{' '}
          <Link href={'/solar-financing-lease-loan'}>
            What is the best way to finance solar?
          </Link>
        </p>
        <H4>What Does the Federal Solar Tax Credit Cover?</H4>
        <p>
          The Federal Solar Tax Credit covers various costs related to
          installing a solar energy system. This includes the solar equipment
          itself, shipping costs, engineer and installer professional service
          fees, electrician fees, and even permit fees.
        </p>
        <div className="overflow-x-auto relative">
          <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
              <tr>
                <th scope="col" className="py-3 px-6">
                  What&apos;s Covered
                </th>
                <th scope="col" className="py-3 px-6">
                  Explanation
                </th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-white border-b dark:bg-gray-800 dark:border-gray-700">
                <td className="py-4 px-6">Solar Equipment</td>
                <td className="py-4 px-6">
                  Includes solar panels, inverter, mounting equipment.
                </td>
              </tr>
              <tr className="bg-gray-50 dark:bg-gray-800 border-b dark:border-gray-700">
                <td className="py-4 px-6">Installation Costs</td>
                <td className="py-4 px-6">
                  Includes labor costs for on-site preparation, assembly, and
                  installation.
                </td>
              </tr>
              <tr className="bg-white dark:bg-gray-800 border-b dark:border-gray-700">
                <td className="py-4 px-6">Professional Service Fees</td>
                <td className="py-4 px-6">
                  Includes costs for developers, electricians, and installers.
                </td>
              </tr>
              <tr className="bg-gray-50 dark:bg-gray-800 border-b dark:border-gray-700">
                <td className="py-4 px-6">Permitting Service Fees</td>
                <td className="py-4 px-6">
                  Fees related to applications and inspections.
                </td>
              </tr>
              <tr className="bg-white dark:bg-gray-800 dark:border-gray-700">
                <td className="py-4 px-6">Freight Shipping Costs</td>
                <td className="py-4 px-6">
                  Shipping costs for transporting equipment.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <H4>How to Claim Solar Tax Credit?</H4>
        <p>
          To claim the Federal Solar Tax Credit, you&apos;ll need to fill out
          IRS Form 5695 and include the result on IRS Form 1040.
        </p>
        <ol>
          <li>Complete Form 5695 to calculate your credit amount.</li>
          <li>Input this data on your 1040 individual tax return.</li>
          <li>Add Form 5695 to your tax return when filing.</li>
        </ol>
        <p>
          Remember, if your tax credit exceeds your tax liability, the remainder
          can be carried forward to the next tax year.
        </p>
        <H4>How Do Other Incentives Affect the Federal Solar Tax Credit?</H4>
        <p>
          <Link href={'/home-battery-solar-installers-near-me'}>
            State and local solar incentives
          </Link>
          , solar renewable energy certificates (SRECs), and utility rebates can
          help reduce the cost of your solar energy system further. However,
          they don&apos;t impact your Federal Solar Tax Credit directly. The
          federal tax credit is calculated based on the net system cost, i.e.,
          after other incentives have been deducted from the original system
          cost.
        </p>
        <p>
          The Federal Solar Tax Credit is a valuable incentive for homeowners
          and businesses to transition into renewable energy. It&apos;s a
          fantastic opportunity to contribute to a more sustainable future while
          also saving money on your taxes.
        </p>
        <p className="italic">
          Please consult with a tax professional to ensure you meet all
          eligibility requirements and to understand the full implications for
          your individual tax situation.
        </p>
        <H4>Am I Eligible to Claim the Federal Solar Tax Credit?</H4>
        <p>
          Eligibility for the Federal Solar Tax Credit is primarily determined
          by two key factors: ownership and tax liability.
        </p>
        <ol>
          <li>
            <strong>Ownership:</strong> You need to own the solar panel system.
            If you lease your system or have a Power Purchase Agreement (PPA),
            you are not eligible, as the system is not owned by you
          </li>
          <li>
            <strong>Tax Liability:</strong> You need to have a federal tax
            liability to take advantage of the credit. Remember, it’s a tax
            credit, not a rebate, so if you owe no taxes, there is no credit.
          </li>
        </ol>
        <p>
          Keep in mind that the system must be installed and operational by
          December 31 of the tax year for which you’re claiming the credit.
          Additionally, the tax credit is nonrefundable, meaning you won&apos;t
          receive a refund if the credit is larger than your tax liability.
          However, you can carry over the unused portion of your credit to the
          following year.
        </p>
        <H4>Federal Tax Credit Summary</H4>
        <p>
          The Federal Solar Tax Credit is a valuable incentive (a credit valued
          at 30% of the installed system) to help make the switch to renewable
          energy more affordable. The credit plays a key role in helping to
          reduce our carbon footprint while also saving money. We can help you
          understand the local, state and federal incentives you qualify for.
          Learn more below.
        </p>
      </CenteredContentSection>
      <FullWidthGuideFormSection cid="jc7bglxay226vdrn" />
    </article>
  );
}
